import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getImageUrl } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { RefreshCw, Users, Activity, DollarSign, TrendingUp, ChevronRight } from 'lucide-react';

const fmtBRL = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const GymFinancial = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSample, setShowSample] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gym/financial`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;
  if (!data) return <p className="text-center py-12 text-slate-400">Erro ao carregar dados financeiros</p>;

  const s = data.sample_calculation;

  return (
    <div className="space-y-6" data-testid="gym-financial">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Financeiro</h1>
          <p className="text-slate-500 text-sm mt-0.5">Repasses atualizados diariamente</p>
        </div>
        <Button variant="outline" onClick={() => { setLoading(true); axios.get(`${API}/gym/financial`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false)); }} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Repasse Total</p>
            </div>
            <p className="text-2xl font-black text-emerald-700" data-testid="total-repasse">{fmtBRL(data.total_repasse)}</p>
            <p className="text-xs text-slate-500 mt-1">Vinculos + Check-ins extras</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Alunos Vinculados</p>
            </div>
            <p className="text-2xl font-black text-blue-700" data-testid="bound-count">{data.total_bound_students}</p>
            <p className="text-xs text-slate-500 mt-1">{data.active_bindings} ativo(s) | Repasse: {fmtBRL(data.total_repasse_70)}</p>
          </CardContent>
        </Card>
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center"><Activity className="w-5 h-5 text-violet-600" /></div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Check-ins Extras</p>
            </div>
            <p className="text-2xl font-black text-violet-700" data-testid="extra-checkins">{data.total_extra_checkins}</p>
            <p className="text-xs text-slate-500 mt-1">Repasse: {fmtBRL(data.total_checkin_repasse)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Alunos Vinculados — Repasse 70%</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.students.length === 0 ? (
            <p className="text-center py-6 text-slate-400 text-sm">Nenhum aluno vinculado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="gym-fin-students-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aluno</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Plano</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Repasse (70%)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Dias</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map(st => (
                    <tr key={st.student_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {st.profile_photo ? (
                            <img src={getImageUrl(st.profile_photo)} alt="" className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">{st.name?.charAt(0)}</div>
                          )}
                          <span className="font-medium text-slate-900 truncate">{st.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{st.company_name}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700">{st.plan_name}</span></td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">{fmtBRL(st.gym_share_70)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.binding_status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : st.binding_status === 'expirado' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                          {st.binding_status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600 hidden sm:table-cell">{st.days_remaining}d</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan={3} className="px-4 py-3 font-bold text-slate-700">Total</td>
                    <td className="px-4 py-3 text-right font-black text-emerald-700 text-base">{fmtBRL(data.total_repasse_70)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-100">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Check-ins Extras — Repasse por Uso</CardTitle>
            <span className="text-sm font-bold text-violet-700">{fmtBRL(data.total_checkin_repasse)}</span>
          </div>
        </CardHeader>
        <CardContent>
          {data.checkins.length === 0 ? (
            <p className="text-center py-6 text-slate-400 text-sm">Nenhum check-in extra realizado</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.checkins.map((ck, i) => (
                <div key={ck.checkin_id || ck.timestamp || i} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2.5 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-[10px] font-bold text-violet-700">#{ck.checkin_number}</span>
                    <div>
                      <p className="font-medium text-slate-900">{ck.student_name}</p>
                      <p className="text-[11px] text-slate-400">{ck.timestamp ? new Date(ck.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'} {ck.modality && `| ${ck.modality}`}</p>
                    </div>
                  </div>
                  <span className="font-bold text-violet-700">{fmtBRL(ck.value)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-white" data-testid="sample-calculation">
        <CardContent className="p-5">
          <button onClick={() => setShowSample(!showSample)} className="flex items-center justify-between w-full text-left">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Como funciona o calculo?</p>
                <p className="text-xs text-slate-500">Modelo de exemplo: {s.plan_name} ({fmtBRL(s.plan_price)})</p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showSample ? 'rotate-90' : ''}`} />
          </button>

          {showSample && (
            <div className="mt-4 pt-4 border-t border-amber-200 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] text-slate-500 uppercase">Valor do Plano</p>
                  <p className="text-lg font-black text-slate-900">{fmtBRL(s.plan_price)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-blue-200">
                  <p className="text-[10px] text-blue-500 uppercase">Academia (70%)</p>
                  <p className="text-lg font-black text-blue-700">{fmtBRL(s.gym_share_70)}</p>
                  <p className="text-[10px] text-slate-400">Repasse direto ao vincular</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-violet-200">
                  <p className="text-[10px] text-violet-500 uppercase">Reserva CK (30%)</p>
                  <p className="text-lg font-black text-violet-700">{fmtBRL(s.checkin_reserve_30)}</p>
                  <p className="text-[10px] text-slate-400">Pago por check-in extra</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Regra de Pagamento por Check-in</p>
                <p className="text-sm text-slate-700 mb-3">
                  De <strong>1 a {s.rules.fixed_limit}</strong> check-ins: <strong className="text-emerald-700">{fmtBRL(s.rules.fixed_price)}</strong> por check-in.
                  A partir de <strong>{s.rules.fixed_limit + 1}</strong>: o pool de <strong className="text-violet-700">{fmtBRL(s.rules.pool_total)}</strong> e dividido igualmente. Max: <strong>{s.rules.max_checkins}</strong> check-ins.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="py-1.5 text-left text-slate-500">CK</th>
                        <th className="py-1.5 text-right text-slate-500">Valor/CK</th>
                        <th className="py-1.5 text-right text-slate-500">Total Acumulado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.breakdown.map(b => (
                        <tr key={b.checkin_number} className="border-b border-slate-50">
                          <td className="py-1.5 font-medium">{b.checkin_number}x</td>
                          <td className="py-1.5 text-right font-semibold text-violet-700">{fmtBRL(b.value_per_checkin)}</td>
                          <td className="py-1.5 text-right text-slate-600">{fmtBRL(b.total_accumulated)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GymFinancial;
